import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import {
  Breadcrumbs,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Link as UILink,
} from "@mui/material";
import {
  Campaign,
  DynamicForm,
  Group,
  AdminPanelSettings,
  ArrowBack,
  OpenInNew,
  Dashboard,
  Settings,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ModalProgress } from "../Components/ModalProgress";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { useQuery } from "@apollo/client";
import { GET_ROLE, RoleResult } from "../Queries";
import { FEATURES } from "../features";

export interface Props {
  actionComponent?: React.ReactNode;
  breadcrumbs?: Breadcrumb[];
  loading?: boolean;
  studentVariant?: boolean;
  children: React.ReactNode;
  window?: () => Window;
}

export interface Breadcrumb {
  title: string;
  link?: string;
}

const drawerWidth = 240;

function Logo() {
  // TODO: Fix chart themes so we can restore auto dark mode
  // const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const prefersDarkMode = true;

  return (
    <Box sx={{ ml: "auto", mr: "auto" }}>
      <img
        src={prefersDarkMode ? "/logo-dark.svg" : "/logo-light.svg"}
        alt="Feedback Loop Logo"
        aria-label="Feedback Loop Logo"
        width="170"
      />
    </Box>
  );
}

function InstructorDrawerContents() {
  const { t } = useTranslation();
  const { data: roleData } = useQuery<RoleResult>(GET_ROLE);
  const isInstanceAdmin = roleData?.isInstanceAdmin;
  const hasCourseContext = roleData?.hasCourseContext;
  const isInstructor = roleData?.myRole === "instructor";
  const isInDashboard = window.location.pathname.startsWith("/dashboard");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <List>
        <ListItem>
          <Logo />
        </ListItem>
        {isInDashboard ? (
          // Dashboard Navigation
          isInstanceAdmin && (
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/dashboard" selected={true}>
                <ListItemIcon>
                  <Dashboard />
                </ListItemIcon>
                <ListItemText primary={t("navigation.dashboard")} />
              </ListItemButton>
            </ListItem>
          )
        ) : (
          // Course Navigation
          <>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/surveys"
                selected={window.location.pathname.startsWith("/surveys")}
              >
                <ListItemIcon>
                  <Campaign />
                </ListItemIcon>
                <ListItemText primary={t("navigation.surveys")} />
              </ListItemButton>
            </ListItem>
            {FEATURES.ASSIGNMENTS_REVIEW_ENABLED && (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/assignments"
                  selected={window.location.pathname.startsWith("/assignments")}
                >
                  <ListItemIcon>
                    <DynamicForm />
                  </ListItemIcon>
                  <ListItemText primary={t("navigation.assignments")} />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/teams"
                selected={window.location.pathname.startsWith("/teams")}
              >
                <ListItemIcon>
                  <Group />
                </ListItemIcon>
                <ListItemText primary={t("navigation.teams")} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                component={Link}
                to="/templates"
                selected={window.location.pathname.startsWith("/templates")}
              >
                <ListItemIcon>
                  <DynamicForm />
                </ListItemIcon>
                <ListItemText primary={t("navigation.templates")} />
              </ListItemButton>
            </ListItem>
          </>
        )}
      </List>

      {/* Context Switch Links */}
      <List sx={{ marginTop: "auto" }}>
        {isInDashboard && hasCourseContext && (
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/surveys">
              <ListItemIcon>
                <ArrowBack />
              </ListItemIcon>
              <ListItemText primary={t("navigation.courseView")} />
            </ListItemButton>
          </ListItem>
        )}
        {!isInDashboard && isInstructor && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/settings"
              selected={window.location.pathname.startsWith("/settings")}
            >
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText primary={t("navigation.settings")} />
            </ListItemButton>
          </ListItem>
        )}
        {!isInDashboard && isInstanceAdmin && (
          <ListItem disablePadding>
            <ListItemButton component={Link} to="/dashboard">
              <ListItemIcon>
                <OpenInNew />
              </ListItemIcon>
              <ListItemText primary={t("navigation.adminTools")} />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );
}

function StudentDrawerContents() {
  const { t } = useTranslation();
  return (
    <List>
      <ListItem>
        <Logo />
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton
          component={Link}
          to="/surveys"
          selected={window.location.pathname.startsWith("/surveys")}
        >
          <ListItemIcon>
            <Campaign />
          </ListItemIcon>
          <ListItemText primary={t("navigation.surveys")} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton
          component={Link}
          to="/assignments"
          selected={window.location.pathname.startsWith("/assignments")}
        >
          <ListItemIcon>
            <DynamicForm />
          </ListItemIcon>
          <ListItemText primary={t("navigation.assignments")} />
        </ListItemButton>
      </ListItem>
    </List>
  );
}

function getTitle(crumbs: Breadcrumb[]) {
  const pageTitle = crumbs[crumbs.length - 1].title || "";
  const title = "Feedback Loop";
  if (pageTitle) {
    return `${title} > ${pageTitle}`;
  }
  return title;
}

export function Layout({
  actionComponent = undefined,
  breadcrumbs,
  children,
  studentVariant = false,
  loading = false,
  window,
}: Props) {
  const { message } = useErrorBoundary();
  const [auth] = React.useState(false);
  const [drawer, setDrawer] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  React.useEffect(() => {
    document.title = getTitle(breadcrumbs || []);
  }, [breadcrumbs]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawer(!drawer);
  };

  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            sx={{ mr: 1, display: { sm: "none" } }}
            onClick={() => setDrawer(true)}
          >
            <MenuIcon />
          </IconButton>

          {breadcrumbs?.length && (
            <Breadcrumbs sx={{ flexGrow: 1, mr: "20px", color: "white" }}>
              {breadcrumbs.map(({ link, title }, index) => (
                <UILink
                  key={index}
                  component={Link}
                  underline="hover"
                  color="inherit"
                  to={link || "#"}
                >
                  {title}
                </UILink>
              ))}
            </Breadcrumbs>
          )}

          {actionComponent}

          {auth && (
            <div>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleClose}>Profile</MenuItem>
                <MenuItem onClick={handleClose}>My account</MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
        <Drawer
          container={container}
          variant="temporary"
          open={drawer}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {studentVariant ? (
            <StudentDrawerContents />
          ) : (
            <InstructorDrawerContents />
          )}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {studentVariant ? (
            <StudentDrawerContents />
          ) : (
            <InstructorDrawerContents />
          )}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {loading && <ModalProgress />}
        {children}
      </Box>
    </Box>
  );
}
